"use client";
import { capitalize } from "@/lib/utils";
import {
	type UseStoreBuilderContextResult,
	useStoreBuilderContext,
} from "@/ui/admin/store-builder/store-builder-context";
import { asyncDebounce, debounce } from "@/ui/hooks/use-debounced-value";
import { Button } from "@/ui/shadcn/button";
import { Label } from "@/ui/shadcn/label";
import { Switch } from "@/ui/shadcn/switch";
import { StatefulColorPicker } from "@/ui/stateful-color-picker";
import { StatefulInput } from "@/ui/stateful-input";
import { nanoid } from "nanoid";
import { useState } from "react";
import { useFormStatus } from "react-dom";
import { MenuEditor } from "../nav/menu-editor";
import type { YnsComponentForm } from "../types";
import type { Footer, FooterSection } from "./footer";

const debouncedSaveSections = asyncDebounce(
	(context: UseStoreBuilderContextResult<typeof Footer>, sections: FooterSection[]) => {
		return context.saveSelectedPageSectionField({
			fieldPath: "sections",
			value: sections,
		});
	},
	300,
);

export const FooterForm: YnsComponentForm<typeof Footer> = ({ data, theme }) => {
	const [sections, setSections] = useState(data.sections ?? []);
	const context = useStoreBuilderContext<typeof Footer>();

	const { pending } = useFormStatus();

	return (
		<div className="grid gap-4 relative pb-20">
			<Label className="grid gap-2">
				<span>Company name</span>
				<StatefulInput
					defaultValue={data.name}
					name="name"
					onBlur={(e) => {
						if (e.currentTarget.value === data.name) {
							return;
						}
						context.saveSelectedPageSectionField({ fieldPath: "name", value: e.currentTarget.value });
					}}
					data-1p-ignore
					autoComplete="off"
				/>
			</Label>
			<Label className="grid gap-2">
				<span>Tagline (below name)</span>
				<StatefulInput
					defaultValue={data.tagline}
					name="tagline"
					onBlur={(e) => {
						if (e.currentTarget.value === data.tagline) {
							return;
						}
						context.saveSelectedPageSectionField({ fieldPath: "tagline", value: e.currentTarget.value });
					}}
					data-1p-ignore
					autoComplete="off"
				/>
			</Label>
			<Label className="flex items-center gap-2">
				<Switch
					defaultChecked={data.credits ?? false}
					name="credits"
					onCheckedChange={(value) => {
						context.saveSelectedPageSectionField({ fieldPath: "credits", value });
					}}
				/>
				<span>Show credits</span>
			</Label>

			{(["instagram", "facebook", "youtube", "linkedin", "x"] as const).map((social) => (
				<Label className="grid gap-2" key={social}>
					<span>{capitalize(social)}</span>
					<StatefulInput
						defaultValue={data.socials?.[social]}
						name={`socials.${social}`}
						placeholder={`Your ${capitalize(social)} handle`}
						onBlur={async (e) => {
							const value = e.currentTarget.value;
							if (value === data.socials?.[social]) {
								return;
							}
							console.log({ "data.socials": data.socials });
							if (!data.socials) {
								await context.saveSelectedPageSectionField({ fieldPath: "socials", value: {} });
							}
							await context.saveSelectedPageSectionField({
								fieldPath: `socials.${social}`,
								value,
							});
						}}
						data-1p-ignore
						autoComplete="off"
					/>
				</Label>
			))}
			<Label className="grid gap-2 w-max">
				<span>Background Color</span>
				<StatefulColorPicker
					defaultValue={theme.backgroundColor}
					onValueChange={debounce(
						(value) => {
							context.saveSelectedPageSectionThemeField({
								fieldPath: "backgroundColor",
								value,
							});
						},
						300,
						{ trailing: true },
					)}
				/>
			</Label>
			<Label className="grid gap-2 w-max">
				<span>Color</span>
				<StatefulColorPicker
					defaultValue={theme.color}
					onValueChange={debounce(
						(value) => {
							context.saveSelectedPageSectionThemeField({
								fieldPath: "color",
								value,
							});
						},
						300,
						{ trailing: true },
					)}
				/>
			</Label>
			<fieldset className="grid gap-4">
				<legend className="text-sm font-medium leading-none mb-2">Links</legend>
				<div className="flex flex-col gap-6">
					{sections?.map((section, sectionIndex) => (
						<fieldset key={sectionIndex + "_" + sections.length} className="grid gap-4">
							<div className="relative">
								<Button
									variant="link"
									className="p-0 text-destructive/50 hover:text-destructive absolute right-0 -top-3"
									type="button"
									onClick={() => {
										const newSections = sections.filter((_, i) => i !== sectionIndex);
										setSections(newSections);
										debouncedSaveSections(context, newSections);
									}}
								>
									Remove
								</Button>
								<Label className="grid gap-2">
									<span>Section {sectionIndex + 1} header</span>
									<StatefulInput
										defaultValue={section.header}
										name={`sections.${sectionIndex}.header`}
										onValueChange={debounce(
											(value) => {
												const newSections = sections.map((section, idx) =>
													idx === sectionIndex ? { ...section, header: value } : section,
												);
												setSections(newSections);
												debouncedSaveSections(context, newSections);
											},
											300,
											{ trailing: true },
										)}
									/>
								</Label>
							</div>
							<MenuEditor
								onChange={(item) => {
									const newSections = sections.map((section, idx) =>
										idx === sectionIndex ? { ...section, links: item } : section,
									);
									setSections(newSections);
									debouncedSaveSections(context, newSections);
								}}
								allowedTypes={["custom", "category", "collection", "custom", "static"]}
								items={
									section.links?.map((link) => {
										// legacy links
										if (!("type" in link)) {
											return {
												id: nanoid(),
												type: "custom",
												label: link.label ?? "",
												url: link.href ?? "",
											};
										}
										return link;
									}) ?? []
								}
							/>
						</fieldset>
					))}
				</div>
				<Button
					type="button"
					variant="outline"
					size="sm"
					onClick={() => {
						const newSections = [...sections, { header: "", links: [{ label: "", href: "" }] }];
						setSections(newSections);
						debouncedSaveSections(context, newSections);
					}}
				>
					Add section
				</Button>
			</fieldset>
		</div>
	);
};
